import { useCallback, useEffect, useMemo } from "react";
import { getPRD4010_R01List, getLatestPRD4010_R01 } from "./Api_PRD4010_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { usePRD4010_R01Store } from "./Store_PRD4010_R01";
import { PRD4010_R01Dto, PRD4010_R01CardData } from "./Dto_PRD4010_R01";

interface Props {
    setMessage: (message: string) => void;
}

const toNum = (v: unknown): number => {
    if (v === null || v === undefined) return 0;
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
};

/** workplaceCd(또는 plcid) 기준으로 묶고, addressid 오름차순으로 입고/생산 2행을 카드 1개로 변환 */
export const groupToCards = (list: PRD4010_R01Dto[]): PRD4010_R01CardData[] => {
    const map = new Map<string, PRD4010_R01Dto[]>();

    list.forEach((item) => {
        const key = item.workplaceCd || item.plcid || "";
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(item);
    });

    const cards: PRD4010_R01CardData[] = [];

    map.forEach((rows) => {
        const sorted = [...rows].sort(
            (a, b) => toNum(a.addressid) - toNum(b.addressid)
        );
        const inRow = sorted[0];
        const outRow = sorted[1];

        const inQty = toNum(inRow?.productionQty);
        const outQty = toNum(outRow?.productionQty);

        cards.push({
            workplaceCd: inRow?.workplaceCd ?? "",
            workplaceNm: inRow?.workplaceNm ?? "",
            inQty,
            productionQty: outQty,
            createdatetime: inRow?.createdatetime ?? "",
            itemNm: inRow?.itemNm ?? "",
            itemSpec: inRow?.itemSpec ?? "",
            empNm: inRow?.empNm ?? "",
            fromTime: inRow?.fromTime ?? "",
            hasPlcData:
                rows.length > 0 &&
                (toNum(inRow?.productionQty) > 0 || toNum(outRow?.productionQty) > 0),
        });
    });

    return cards;
};

export const Content_PRD4010_R01 = (props: Props) => {
    const { setMessage } = props;

    const {
        PRD4010_R01Filter,
        PRD4010_R01CardList,
        setPRD4010_R01List,
        setPRD4010_R01CardList,
        initData,
    } = usePRD4010_R01Store();
    const { setLoadingSpinner } = useLoadingSpinnerStore();

    useFetchActionbarAuth("PRD4010_R01");

    const defaultCards = useMemo<PRD4010_R01CardData[]>(
        () => [
            {
                workplaceCd: "A",
                workplaceNm: "A라인 생산",
                inQty: 0,
                productionQty: 0,
                createdatetime: "",
                itemNm: "",
                itemSpec: "",
                empNm: "",
                fromTime: "",
                hasPlcData: false,
            },
            {
                workplaceCd: "B",
                workplaceNm: "B라인 생산",
                inQty: 0,
                productionQty: 0,
                createdatetime: "",
                itemNm: "",
                itemSpec: "",
                empNm: "",
                fromTime: "",
                hasPlcData: false,
            },
            {
                workplaceCd: "C",
                workplaceNm: "C라인 생산",
                inQty: 0,
                productionQty: 0,
                createdatetime: "",
                itemNm: "",
                itemSpec: "",
                empNm: "",
                fromTime: "",
                hasPlcData: false,
            },
            {
                workplaceCd: "PBA",
                workplaceNm: "PBA 생산",
                inQty: 0,
                productionQty: 0,
                createdatetime: "",
                itemNm: "",
                itemSpec: "",
                empNm: "",
                fromTime: "",
                hasPlcData: false,
            },
        ],
        []
    );

    const renderedCards = useMemo(
        () => (PRD4010_R01CardList.length > 0 ? PRD4010_R01CardList : defaultCards),
        [PRD4010_R01CardList, defaultCards]
    );

    const fetchData = useCallback(
        async (isAutoRefresh = false) => {
            setLoadingSpinner(true);
            try {
                const response = await getPRD4010_R01List("search", PRD4010_R01Filter);
                const data = response.data.data as PRD4010_R01Dto[];

                if (!data || data.length === 0) {
                    setPRD4010_R01List([]);
                    setPRD4010_R01CardList([]);
                    showWarning("조회된 데이터가 없습니다.");
                    return;
                }

                setPRD4010_R01List(data);
                setPRD4010_R01CardList(groupToCards(data));

                if (!isAutoRefresh) {
                    ShowSuccessFetch(setMessage, `조회 완료 (${data.length}건)`);
                }
            } catch (error) {
                console.error("조회 실패:", error);
                showWarning("데이터 조회에 실패했습니다.");
            } finally {
                setLoadingSpinner(false);
            }
        },
        [
            PRD4010_R01Filter,
            setPRD4010_R01List,
            setPRD4010_R01CardList,
            setMessage,
            setLoadingSpinner,
        ]
    );

    const fetchLatestData = useCallback(async () => {
        try {
            const response = await getLatestPRD4010_R01(PRD4010_R01Filter.compCd);
            const data = response.data.data as PRD4010_R01Dto[];
            if (data && data.length > 0) {
                setPRD4010_R01List(data);
                setPRD4010_R01CardList(groupToCards(data));
                ShowSuccessFetch(setMessage, `자동조회 완료 (${data.length}건)`);
            }
        } catch (error) {
            console.error("자동조회 실패:", error);
            showWarning("자동조회에 실패했습니다.");
        }
    }, [
        PRD4010_R01Filter.compCd,
        setPRD4010_R01List,
        setPRD4010_R01CardList,
        setMessage,
    ]);

    const handleInitClick = useCallback(() => {
        initData();
        setMessage("초기화되었습니다.");
    }, [initData, setMessage]);

    // 사업장·작업일자 준비 시 자동 조회
    useEffect(() => {
        if (!PRD4010_R01Filter.compCd) return;
        void fetchData(false);
    }, [PRD4010_R01Filter.compCd, PRD4010_R01Filter.searchDate, fetchData]);

    useEffect(() => {
        const { autoRefreshInterval } = PRD4010_R01Filter;
        if (autoRefreshInterval === null) return;

        const ms = autoRefreshInterval * 60 * 1000;
        const intervalId = setInterval(() => {
            void fetchLatestData();
        }, ms);

        return () => clearInterval(intervalId);
    }, [PRD4010_R01Filter.autoRefreshInterval, fetchLatestData]);

    return { fetchData, fetchLatestData, handleInitClick, renderedCards };
};
